import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, CircularProgress, TextField, Typography, Alert,
  InputAdornment, IconButton,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonIcon from "@mui/icons-material/Person";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../Store/hooks";
import { useNotifier } from "../../Core/Notifier";
import { useLoginMutation } from "../../Api/attoDeskApi";
import { useFormik } from "formik";
import { IAuth } from "../../Api/Interface/api.interface";
import { setCurrentUser, setEnableAuth } from "../../Store/Auth/AuthSlice";
import cacartLogo from "../../Images/logo_main_black.webp";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const LARAVEL_API_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();
// Imported asset → webpack bundles to /static/media/... regardless of host base path.
const FALLBACK_LOGO = cacartLogo as string;
const BRAND_COLOR = "#0f172a";
const BRAND_GRADIENT = "linear-gradient(160deg, #0f172a 0%, #1e293b 40%, #004d54 100%)";
const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "#f8f9ff" } };

type Mode = "login" | "forgot" | "forgot_done";

type LoginErrorKind = "auth" | "notfound" | "server" | "network" | "unknown";

/**
 * Classify an RTK Query login error so the UI can show an accurate message.
 * RTK surfaces HTTP failures as a numeric `status`, and transport failures as
 * the string statuses FETCH_ERROR / TIMEOUT_ERROR / PARSING_ERROR.
 *   - 401/403/422 → auth rejection (wrong credentials)
 *   - 404         → the login endpoint is missing (API base misconfigured)
 *   - 5xx         → server error
 *   - FETCH/TIMEOUT → backend unreachable
 */
const classifyLoginError = (e: any): LoginErrorKind => {
  const s = e?.status;
  if (s === 401 || s === 403 || s === 422) return "auth";
  if (s === 404) return "notfound";
  if (typeof s === "number" && s >= 500) return "server";
  if (s === "FETCH_ERROR" || s === "TIMEOUT_ERROR") return "network";
  return "unknown";
};

const loginErrorMessage = (kind: LoginErrorKind): string => {
  switch (kind) {
    case "notfound":
      return "Login service not found. The app’s API address is misconfigured — please contact support.";
    case "server":
      return "The server ran into a problem. Please try again in a moment.";
    case "network":
      return "Can’t reach the server. Check your connection or confirm the backend is running.";
    default:
      return "Something went wrong while signing in. Please try again.";
  }
};

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const { showErrorMessage } = useNotifier();
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const [portalLoading, setPortalLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [mode, setMode] = useState<Mode>("login");
  const [error, setError] = useState(() => {
    // Surface the reason the user landed here. The RTK 401 interceptor
    // appends ?reason=session_expired when it kicks the user out after
    // a stale/revoked Sanctum token. Read it from the URL so we don't
    // need a redux flag.
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("reason") === "session_expired") {
        return "Your session expired. Please sign in again.";
      }
    } catch { /* ignore */ }
    return "";
  });

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");

  // Company logo (matches the first/default company on the Company page)
  const [companyLogoUrl, setCompanyLogoUrl] = useState<string>(FALLBACK_LOGO);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/company/getAll`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ searchText: "", id: "" }),
        });
        if (!res.ok) return;
        const json = await res.json();
        const list: any[] = Array.isArray(json?.data) ? json.data : [];
        if (!list.length) return;
        const def = list.find((c: any) => c.isDefault === 1) || list[0];
        if (def?.CompanyLogo && !cancelled) {
          setCompanyLogoUrl(`${LARAVEL_API_URL}/${def.CompanyLogo}`);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  const formik = useFormik({
    initialValues: { username: "", password: "" },
    onSubmit: async (values) => {
      setError("");
      // 1. Try admin login first. Only a genuine auth rejection (401/422)
      //    should fall through to the portal attempt; 404/5xx/network are
      //    infrastructure problems and must NOT be reported as bad creds.
      try {
        const loginResponse = await login(values).unwrap();
        if (loginResponse.status) {
          const tokenData = loginResponse.data as IAuth;
          localStorage.setItem("token", tokenData.token);
          if (tokenData.user) {
            dispatch(setCurrentUser(tokenData.user));
          }
          dispatch(setEnableAuth());
          // Honor ?next=… so a 401 redirect lands the user back where
          // they were. Restrict to in-app paths to avoid open redirects.
          let dest = '/dashboard';
          try {
            const next = new URLSearchParams(window.location.search).get('next');
            if (next && next.startsWith('/') && !next.startsWith('//')) dest = next;
          } catch { /* ignore */ }
          navigate(dest, { replace: true });
          return;
        }
        // 200 with status:false is the backend's "invalid credentials"
        // convention → fall through to the portal attempt below.
      } catch (e) {
        const kind = classifyLoginError(e);
        if (kind !== "auth") { setError(loginErrorMessage(kind)); return; }
        // auth rejection → try the portal login with the same credentials.
      }

      // 2. Try portal (shop owner) login with same credentials.
      setPortalLoading(true);
      try {
        const res = await fetch(`${API_URL}/restaurant-portal/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ email: values.username, password: values.password }),
        });
        if (res.status === 404) { setError(loginErrorMessage("notfound")); return; }
        if (res.status >= 500) { setError(loginErrorMessage("server")); return; }
        let data: any = {};
        try { data = await res.json(); } catch { /* non-JSON body */ }
        if (res.ok && data.success) {
          localStorage.setItem("portal_token", data.token);
          localStorage.setItem("portal_shop_id", String(data.shop_id));
          navigate(`/restaurant-portal/${data.shop_id}/dashboard`, { replace: true });
          return;
        }
        // res is a 4xx auth rejection (or success:false) → genuinely bad creds.
      } catch (_) {
        // fetch threw → network / backend unreachable.
        setError(loginErrorMessage("network"));
        return;
      } finally {
        setPortalLoading(false);
      }

      // Both admin and portal rejected the credentials.
      setError("Invalid username or password");
    },
  });

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!forgotEmail) return;
    setForgotLoading(true); setForgotMsg(""); setError("");
    try {
      const res = await fetch(`${API_URL}/restaurant-portal/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      setForgotMsg(data.message || "If this email is registered, a reset link has been generated.");
      setMode("forgot_done");
    } catch {
      setError("An error occurred. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  const formValid = useMemo(() => {
    return formik.values.username !== "" && formik.values.password !== "";
  }, [formik]);

  const anyLoading = isLoading || portalLoading;

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1200, display: "flex", minHeight: "100vh" }}>
      {/* Left panel - branding */}
      <Box sx={{
        display: { xs: "none", md: "flex" }, width: "45%", bgcolor: BRAND_COLOR,
        flexDirection: "column", justifyContent: "center", alignItems: "center", p: 6,
        background: BRAND_GRADIENT,
      }}>
        <Box sx={{ mb: 1, display: "block", lineHeight: 0 }}>
          <img
            src={FALLBACK_LOGO}
            alt="VM display"
            style={{ width: 320, height: "auto", maxHeight: 200, objectFit: "contain", display: "block" }}
          />
        </Box>
        <Typography sx={{ color: "#fff", fontSize: 32, fontWeight: 800, mb: 1.5, textAlign: "center", lineHeight: 1.2 }}>
          Welcome Back
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 15, textAlign: "center", maxWidth: 320, lineHeight: 1.6 }}>
          Create engaging digital signage experiences with centralized content and device management.
        </Typography>
        <Box sx={{ mt: 6, display: "flex", gap: 3 }}>
          {["Digital Signage", "Media Management", "Playlist Management", " Multi-Screen Control"].map(t => (
            <Box key={t} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleOutlineIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.5)" }} />
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.5)", fontWeight: 500 }}>{t}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Right panel - form */}
      <Box sx={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        bgcolor: "#fff", p: { xs: 3, sm: 5 },
      }}>
        <Box sx={{ width: "100%", maxWidth: 400 }}>
          {/* Mobile logo */}
          <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1.5, mb: 4, justifyContent: "center" }}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: BRAND_COLOR, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <StorefrontIcon sx={{ color: "#fff", fontSize: 22 }} />
            </Box>
            <Typography fontWeight={800} fontSize={20} color={BRAND_COLOR}>POS System</Typography>
          </Box>

          {mode === "login" && (
            <>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: BRAND_COLOR, mb: 0.5 }}>
                Sign In
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#999", mb: 3.5 }}>
                Enter your credentials to access your account
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}

              <form onSubmit={formik.handleSubmit}>
                <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Username / Email</Typography>
                <TextField fullWidth size="small" placeholder="Enter your username or email"
                  value={formik.values.username}
                  onChange={formik.handleChange}
                  name="username"
                  required
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
                  sx={{ mb: 2.5, ...inputSx }}
                />
                <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Password</Typography>
                <TextField fullWidth size="small" placeholder="Enter your password"
                  type={showPw ? "text" : "password"}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  name="password"
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment>,
                    endAdornment: <InputAdornment position="end"><IconButton size="small" onClick={() => setShowPw(v => !v)}>{showPw ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}</IconButton></InputAdornment>,
                  }}
                  sx={{ mb: 1.5, ...inputSx }}
                />
                <Box sx={{ textAlign: "right", mb: 3 }}>
                  <Typography component="span" fontSize={13} color={BRAND_COLOR} fontWeight={600}
                    sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    onClick={() => { setMode("forgot"); setError(""); setForgotMsg(""); setForgotEmail(""); }}>
                    Forgot password?
                  </Typography>
                </Box>
                <Button fullWidth type="submit" variant="contained" disabled={!formValid || anyLoading}
                  sx={{
                    borderRadius: 2, py: 1.5, fontWeight: 700, fontSize: 15,
                    bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#1e293b" },
                    textTransform: "none", boxShadow: "0 4px 14px rgba(26,26,46,0.35)",
                  }}>
                  {anyLoading ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Sign In"}
                </Button>
              </form>
            </>
          )}

          {mode === "forgot" && (
            <>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: BRAND_COLOR, mb: 0.5 }}>
                Forgot Password
              </Typography>
              <Typography sx={{ fontSize: 14, color: "#999", mb: 3.5 }}>
                Enter your portal email to receive a reset link
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2, fontSize: 13 }}>{error}</Alert>}

              <form onSubmit={handleForgot}>
                <Typography fontSize={12} fontWeight={600} color="#666" mb={0.75}>Email Address</Typography>
                <TextField fullWidth size="small" placeholder="Enter your email" type="email"
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required
                  InputProps={{ startAdornment: <InputAdornment position="start"><PersonIcon sx={{ fontSize: 18, color: "#bbb" }} /></InputAdornment> }}
                  sx={{ mb: 3, ...inputSx }}
                />
                <Button fullWidth type="submit" variant="contained" disabled={forgotLoading}
                  sx={{
                    borderRadius: 2, py: 1.5, fontWeight: 700,
                    bgcolor: BRAND_COLOR, "&:hover": { bgcolor: "#1e293b" },
                    textTransform: "none",
                  }}>
                  {forgotLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Request Reset Link"}
                </Button>
                <Button fullWidth variant="text" startIcon={<ArrowBackIcon />}
                  onClick={() => { setMode("login"); setError(""); }}
                  sx={{ mt: 1.5, borderRadius: 2, color: "#999", textTransform: "none" }}>
                  Back to login
                </Button>
              </form>
            </>
          )}

          {mode === "forgot_done" && (
            <>
              <Box textAlign="center">
                <CheckCircleOutlineIcon sx={{ fontSize: 56, color: "#2e7d32", mb: 2 }} />
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: BRAND_COLOR, mb: 1 }}>
                  Check Your Email
                </Typography>
                <Typography sx={{ fontSize: 14, color: "#999", mb: 1 }}>
                  {forgotMsg || "If this email is registered, a reset link has been generated."}
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#999", mb: 3 }}>
                  Please contact your supplier to receive the reset link.
                </Typography>
                <Button fullWidth variant="outlined" startIcon={<ArrowBackIcon />}
                  onClick={() => { setMode("login"); setError(""); setForgotMsg(""); }}
                  sx={{ borderRadius: 2, color: BRAND_COLOR, borderColor: "#ccc", textTransform: "none" }}>
                  Back to login
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default React.memo(Login);
